import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { capitalizeFirstLetter } from '../../../helpers/filterNames';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Switch } from '@/new/components/shadcn/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';

export interface PartyFormItem {
  id?: string;
  name: string;
  isClient: boolean;
  role: 'claimant' | 'defendant' | 'other';
  type?: 'person' | 'organisation' | null;
}

interface PartiesListProps {
  parties: PartyFormItem[];
  onChange: (parties: PartyFormItem[]) => void;
}

export function PartiesList({ parties, onChange }: PartiesListProps) {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [addName, setAddName] = useState('');
  const [addRole, setAddRole] = useState<'claimant' | 'defendant' | 'other'>('other');
  const [addType, setAddType] = useState<'person' | 'organisation'>('person');

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState<'claimant' | 'defendant' | 'other'>('other');
  const [editType, setEditType] = useState<'person' | 'organisation'>('person');

  const handleAdd = () => {
    const trimmedName = addName.trim();
    if (!trimmedName) return;
    onChange([...parties, { name: trimmedName, isClient: false, role: addRole, type: addType }]);
    trackCustomEvent(TrackingEventsV2.MatterContextPartyAdd, { role: addRole, type: addType });
    setAddName('');
    setAddRole('other');
    setAddType('person');
    setShowAddModal(false);
  };

  const openEditModal = (index: number) => {
    const party = parties[index];
    setEditingIndex(index);
    setEditName(party.name);
    setEditRole(party.role ?? 'other');
    setEditType(party.type ?? 'person');
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    const trimmedName = editName.trim();
    if (trimmedName) {
      const updated = [...parties];
      updated[editingIndex] = {
        ...updated[editingIndex],
        name: trimmedName,
        role: editRole,
        type: editType,
      };
      onChange(updated);
      trackCustomEvent(TrackingEventsV2.MatterContextPartyEdit, { role: editRole, type: editType });
    }
    setEditingIndex(null);
  };

  const handleDelete = (index: number) => {
    onChange(parties.filter((_, i) => i !== index));
    trackCustomEvent(TrackingEventsV2.MatterContextPartyDelete);
  };

  const toggleClient = (index: number) => {
    const updated = [...parties];
    updated[index] = { ...updated[index], isClient: !updated[index].isClient };
    onChange(updated);
  };

  const hasClaimant = parties.some((p) => p.role === 'claimant');
  const hasDefendant = parties.some((p) => p.role === 'defendant');
  const noClient = parties.every((p) => !p.isClient);
  const showHints = parties.length > 0 && (!hasClaimant || !hasDefendant || noClient);

  return (
    <div>
      <Button size="sm" onClick={() => setShowAddModal(true)}>
        <Icon className="lucide--plus" />
        Add Party
      </Button>

      {/* Hint prompts */}
      <div
        className="grid transition-all duration-150 ease-in-out"
        style={{ gridTemplateRows: showHints ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="mt-4 space-y-1.5">
            <div
              className="grid transition-all duration-150 ease-in-out"
              style={{ gridTemplateRows: !hasClaimant && parties.length > 0 ? '1fr' : '0fr' }}
            >
              <div className="overflow-hidden">
                <p className="flex items-center gap-2 text-sm text-amber-600">
                  <Icon className="lucide--circle-help flex-shrink-0 text-xs" />
                  Which of these parties are the {t('claimants')}?
                </p>
              </div>
            </div>
            <div
              className="grid transition-all duration-150 ease-in-out"
              style={{ gridTemplateRows: !hasDefendant && parties.length > 0 ? '1fr' : '0fr' }}
            >
              <div className="overflow-hidden">
                <p className="flex items-center gap-2 text-sm text-amber-600">
                  <Icon className="lucide--circle-help flex-shrink-0 text-xs" />
                  Which of these parties are the defendants?
                </p>
              </div>
            </div>
            <div
              className="grid transition-all duration-150 ease-in-out"
              style={{ gridTemplateRows: noClient && parties.length > 0 ? '1fr' : '0fr' }}
            >
              <div className="overflow-hidden">
                <p className="flex items-center gap-2 text-sm text-amber-600">
                  <Icon className="lucide--circle-help flex-shrink-0 text-xs" />
                  Are you representing any of these parties?
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {parties.length > 0 && (
        <ul className="mt-4 space-y-2">
          {parties.map((party, i) => (
            <li key={party.id ?? `new-${i}`} className="flex items-center gap-3 text-sm">
              <span className="flex-shrink-0 text-gray-400 tabular-nums">{i + 1}.</span>
              <div
                className="group flex flex-1 cursor-pointer items-center justify-between rounded-md border bg-gray-50 px-3 py-2 transition-colors hover:border-gray-400"
                onClick={() => openEditModal(i)}
              >
                <div className="flex items-center gap-2.5">
                  <span className="text-gray-600">{party.name}</span>
                  {party.role && party.role !== 'other' && (
                    <Chip className={cn(CHIP_COLOURS.default, 'border px-1.5 py-0.5 font-normal')}>
                      {party.role === 'claimant' ? capitalizeFirstLetter(t('claimant')) : 'Defendant'}
                    </Chip>
                  )}
                  {party.type && (
                    <span className="text-xs text-gray-400">
                      {party.type === 'person' ? 'Person' : t('Organisation')}
                    </span>
                  )}
                </div>
                <div className="ml-3 flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(i);
                    }}
                    className="text-gray-300 hover:text-gray-500"
                  >
                    <Icon className="lucide--pencil text-xs" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(i);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Icon className="lucide--x text-xs" />
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-500">
                <Switch checked={party.isClient} onCheckedChange={() => toggleClient(i)} className="scale-90" />
                Client
                <Tooltip>
                  <TooltipTrigger>
                    <Icon className="lucide--circle-question-mark text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent sideOffset={12} side="right">
                    Is this party your client?
                  </TooltipContent>
                </Tooltip>
              </label>
            </li>
          ))}
        </ul>
      )}

      {/* Add Party Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Icon className="lucide--plus" />
              Add Party
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-party-name">Name</Label>
              <Input
                id="add-party-name"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                placeholder="e.g. Acme Corp Ltd"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-party-role">Role</Label>
              <Select
                value={addRole}
                onValueChange={(value) => setAddRole(value as 'claimant' | 'defendant' | 'other')}
              >
                <SelectTrigger id="add-party-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem className="capitalize" value="claimant">
                    {capitalizeFirstLetter(t('claimant'))}
                  </SelectItem>
                  <SelectItem value="defendant">Defendant</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-party-type">Type</Label>
              <Select value={addType} onValueChange={(value) => setAddType(value as 'person' | 'organisation')}>
                <SelectTrigger id="add-party-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="person">Person</SelectItem>
                  <SelectItem value="organisation">{t('Organisation')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={!addName.trim()}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Party Modal */}
      <Dialog open={editingIndex !== null} onOpenChange={(open) => !open && setEditingIndex(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Icon className="lucide--pencil" />
              Edit Party
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-party-name">Name</Label>
              <Input
                id="edit-party-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    saveEdit();
                  }
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-party-role">Role</Label>
              <Select value={editRole} onValueChange={(v) => setEditRole(v as 'claimant' | 'defendant' | 'other')}>
                <SelectTrigger id="edit-party-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem className="capitalize" value="claimant">
                    {t('claimant')}
                  </SelectItem>
                  <SelectItem value="defendant">Defendant</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-party-type">Type</Label>
              <Select value={editType} onValueChange={(v) => setEditType(v as 'person' | 'organisation')}>
                <SelectTrigger id="edit-party-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="person">Person</SelectItem>
                  <SelectItem value="organisation">{t('Organisation')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingIndex(null)}>
              Cancel
            </Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
